import React, { memo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  MdLogout, MdChevronLeft, MdMenu, MdClose,
} from 'react-icons/md';
import { HOSPITAL_NAV, HOSPITAL_NAV_SECTIONS, filterNavForUser } from '../config/hospitalNav';

export default memo(function Sidebar({
  collapsed,
  onToggle,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) {
  const { currentUser, logout, unreadCount, emergencyAlertCount } = useApp();
  const navigate = useNavigate();

  const items = filterNavForUser(HOSPITAL_NAV, currentUser);
  const byPath = Object.fromEntries(items.map(i => [i.path, i]));
  const showLabels = isMobile || !collapsed;

  const handleNavClick = () => {
    if (isMobile && onMobileClose) onMobileClose();
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={[
          'app-sidebar',
          collapsed && !isMobile ? 'is-collapsed' : '',
          isMobile ? 'is-mobile' : '',
          isMobile && mobileOpen ? 'is-open' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Logo */}
        <div className="app-sidebar__head">
          <div className="app-sidebar__logo-wrap">
            <img src="/hcen_logo.png" alt="HCEN" className="app-sidebar__logo" />
            <span className="app-sidebar__logo-dot" />
          </div>
          {showLabels && (
            <div className="app-sidebar__brand">
              <div className="app-sidebar__brand-name">HCEN</div>
              <div className="app-sidebar__brand-sub">Hospital Dashboard</div>
            </div>
          )}
          {isMobile && (
            <button type="button" className="app-sidebar__close" onClick={onMobileClose} aria-label="Close menu">
              <MdClose />
            </button>
          )}
        </div>

        {/* Toggle — desktop / tablet only */}
        {!isMobile && (
          <button type="button" className="app-sidebar__toggle" onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <MdMenu /> : <MdChevronLeft />}
          </button>
        )}

        {/* Nav */}
        <nav className="app-sidebar__nav">
          {HOSPITAL_NAV_SECTIONS.map(section => {
            const sectionItems = section.items.map(p => byPath[p]).filter(Boolean);
            if (!sectionItems.length) return null;
            return (
              <div key={section.label} className="app-sidebar__section">
                {showLabels && (
                  <div className="app-sidebar__section-label">{section.label}</div>
                )}
                {!showLabels && sectionItems.length > 0 && (
                  <div className="app-sidebar__section-divider" />
                )}
                {sectionItems.map(({ path, icon: Icon, label, emergency }) => {
                  const tooltipLabel = !showLabels
                    ? label === 'Notifications' && unreadCount > 0 ? `${label} · ${unreadCount} unread`
                    : emergency && emergencyAlertCount > 0 ? `${label} · ${emergencyAlertCount} active`
                    : label
                    : undefined;

                  return (
                    <NavLink
                      key={path}
                      to={path}
                      title={tooltipLabel}
                      onClick={handleNavClick}
                      className={({ isActive }) => [
                        'app-sidebar__link',
                        isActive ? 'is-active' : '',
                        emergency ? 'is-emergency' : '',
                        !showLabels ? 'is-icon-only' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && showLabels && (
                            <span className={`app-sidebar__link-bar${emergency ? ' is-emergency' : ''}`} />
                          )}
                          <Icon className="app-sidebar__link-icon" />
                          {showLabels && <span className="app-sidebar__link-text">{label}</span>}

                          {label === 'Notifications' && unreadCount > 0 && showLabels && (
                            <span className="app-sidebar__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                          )}
                          {label === 'Notifications' && unreadCount > 0 && !showLabels && (
                            <span className="app-sidebar__badge app-sidebar__badge--dot">{unreadCount > 9 ? '9+' : unreadCount}</span>
                          )}
                          {emergency && emergencyAlertCount > 0 && showLabels && (
                            <span className="app-sidebar__badge app-sidebar__badge--danger anim-pulse">
                              {emergencyAlertCount > 9 ? '9+' : emergencyAlertCount}
                            </span>
                          )}
                          {emergency && emergencyAlertCount > 0 && !showLabels && (
                            <span className="app-sidebar__badge app-sidebar__badge--dot app-sidebar__badge--danger anim-pulse">
                              {emergencyAlertCount > 9 ? '9+' : emergencyAlertCount}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User card */}
        <div className="app-sidebar__foot">
          {showLabels && (
            <div className="app-sidebar__user">
              <div className="app-sidebar__avatar">
                {currentUser?.name?.charAt(0)?.toUpperCase() || 'H'}
              </div>
              <div className="app-sidebar__user-info">
                <div className="app-sidebar__user-name">
                  {currentUser?.hospitalRole === 'receptionist' ? (currentUser?.hospital || currentUser?.name) : currentUser?.name}
                </div>
                <div className="app-sidebar__user-sub">
                  {currentUser?.hospitalRole === 'receptionist' ? (currentUser?.name || 'Receptionist') : (currentUser?.hospital || '')}
                </div>
                <span className={`badge ${currentUser?.hospitalRole === 'owner' ? 'badge-primary' : 'badge-secondary'}`} style={{ fontSize: 9.5, padding: '1px 7px', marginTop: 2 }}>
                  {currentUser?.hospitalRole === 'owner' ? 'Owner' : currentUser?.hospitalRole === 'receptionist' ? 'Receptionist' : 'Hospital'}
                </span>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => { logout(); navigate('/login'); onMobileClose?.(); }}
            className="btn btn-secondary btn-sm app-sidebar__logout"
          >
            <MdLogout style={{ fontSize: 15 }} />
            {showLabels && 'Logout'}
          </button>
        </div>
      </aside>
    </>
  );
});
